// Right details panel — metadata for the selected title with the rating
// workflow pinned to the bottom.
//
//   title (h2)
//   type · year · runtime min
//   [genre] [genre] …
//   directed by …
//   cast, comma, list
//   synopsis (scrolls)
//   ─────────────────
//   on queue · unrated              ← current state line
//   [ + watch list ]                ← full-width (double) action button
//   [0 not watched][5 recommend]    ← 2×5 grid: 0–4 down left, 5–9 down right
//   [1 endured   ][6 timeless ] …
//
// Rating a title can move it between lists; after a successful POST the
// affected list caches + counts are revalidated and the URL ?list= param
// follows the item to its new list.

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSWRConfig } from "swr";

import { apiJson } from "../api/client";
import { RATING_LABELS, type ListItem, type ListName, type TitleDetail } from "../api/types";

import "./DetailsPanel.css";

export interface RateModeState {
  position: number;
  total: number;
  onExit: () => void;
}

export function DetailsPanel({
  imdbId,
  title,
  error,
  isLoading,
  rateMode = null,
  siblings = [],
}: {
  imdbId: string | null;
  title: TitleDetail | undefined;
  error: Error | undefined;
  isLoading: boolean;
  rateMode?: RateModeState | null;
  // Ordered items of the currently selected (source) list, so that after an
  // action the selection can advance to the next item in that list.
  siblings?: ListItem[];
}) {
  if (rateMode && rateMode.total === 0 && !imdbId) {
    return (
      <div className="details details--empty">
        <div>
          <p>queue is empty — every suggestion is rated.</p>
          <p className="details__hint">run analyze to get new suggestions.</p>
          <button type="button" className="rate-mode__exit" onClick={rateMode.onExit}>
            done
          </button>
        </div>
      </div>
    );
  }
  if (!imdbId) {
    return <div className="details details--empty">no title selected</div>;
  }
  if (error) {
    return <div className="details details--empty details--error">couldn't load title</div>;
  }
  if (isLoading || !title) {
    return <div className="details details--empty">loading…</div>;
  }
  return <TitleDetails title={title} rateMode={rateMode} siblings={siblings} />;
}

function TitleDetails({
  title,
  rateMode,
  siblings,
}: {
  title: TitleDetail;
  rateMode: RateModeState | null;
  siblings: ListItem[];
}) {
  const { mutate } = useSWRConfig();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);

  // After an action, keep the cursor in the SOURCE list and advance to the next
  // item there (the one after this, or the previous if this was last), rather
  // than following the item into its new list. Rate mode has its own queue
  // advancement in the Navigator, so it's left untouched here.
  function advanceInSource(prevList: ListName | null, moved: boolean) {
    if (rateMode) return;
    const idx = siblings.findIndex((s) => s.imdb_id === title.imdb_id);
    const nextId =
      idx < 0
        ? null
        : idx + 1 < siblings.length
          ? siblings[idx + 1].imdb_id
          : idx - 1 >= 0
            ? siblings[idx - 1].imdb_id
            : null;

    const next = new URLSearchParams(params);
    if (prevList) next.set("list", prevList);
    if (nextId) {
      next.set("title", nextId);
    } else if (moved) {
      // Nothing left in the source list to advance to.
      next.delete("title");
    } else {
      // Not moved and no sibling (only item): keep it selected.
      return;
    }
    setParams(next, { replace: true });
  }

  async function onRate(rating: number) {
    if (busy) return;
    setBusy(true);
    setRateError(null);
    const prevList = title.list;
    try {
      const updated = await apiJson<TitleDetail>(`/api/titles/${title.imdb_id}/rate`, {
        method: "POST",
        body: JSON.stringify({ rating }),
      });
      // Seed the detail cache with the server response, then revalidate the
      // affected lists + counts.
      await mutate(`/api/titles/${title.imdb_id}`, updated, { revalidate: false });
      void mutate("/api/lists");
      if (prevList) void mutate(`/api/lists/${prevList}`);
      const moved = !!updated.list && updated.list !== prevList;
      if (moved) void mutate(`/api/lists/${updated.list}`);
      advanceInSource(prevList, moved);
    } catch {
      setRateError("rating failed — try again");
    } finally {
      setBusy(false);
    }
  }

  async function onWatchlist() {
    if (busy) return;
    setBusy(true);
    setRateError(null);
    const prevList = title.list;
    try {
      const updated = await apiJson<TitleDetail>(`/api/titles/${title.imdb_id}/watchlist`, {
        method: "POST",
      });
      await mutate(`/api/titles/${title.imdb_id}`, updated, { revalidate: false });
      void mutate("/api/lists");
      if (prevList) void mutate(`/api/lists/${prevList}`);
      void mutate("/api/lists/watchlist");
      const moved = !!updated.list && updated.list !== prevList;
      advanceInSource(prevList, moved);
    } catch {
      setRateError("could not add to watch list");
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    if (busy || title.list == null) return;
    setBusy(true);
    setRateError(null);
    const prevList = title.list;
    try {
      const updated = await apiJson<TitleDetail>(`/api/titles/${title.imdb_id}/remove`, {
        method: "POST",
      });
      await mutate(`/api/titles/${title.imdb_id}`, updated, { revalidate: false });
      void mutate("/api/lists");
      if (prevList) void mutate(`/api/lists/${prevList}`);
      advanceInSource(prevList, true); // it left every list
    } catch {
      setRateError("could not remove");
    } finally {
      setBusy(false);
    }
  }

  // Keyboard shortcuts (active whenever a title is selected): 0–9 rate, w adds
  // to the watch list — mirrors the on-screen buttons, including their disabled
  // rules. Ignored while typing in a field or with a modifier held.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      if (busy) return;
      if (e.key >= "0" && e.key <= "9") {
        const n = Number(e.key);
        // 0 is "not watched" — no-op on the ignore list or when off all lists.
        if (n === 0 && (title.list === "ignore" || title.list == null)) return;
        e.preventDefault();
        void onRate(n);
      } else if (e.key === "w" || e.key === "W") {
        if (title.list === "watchlist" || title.list === "watched") return;
        e.preventDefault();
        void onWatchlist();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title.imdb_id, title.list, busy]);

  const meta = [
    title.title_type,
    title.year != null ? String(title.year) : null,
    title.runtime_minutes != null ? `${title.runtime_minutes} min` : null,
  ].filter(Boolean);

  return (
    <div className="details">
      <div className="details__scroll">
        <h2 className="details__title">{title.title}</h2>
        {meta.length > 0 && <div className="details__meta">{meta.join(" · ")}</div>}

        {title.genres.length > 0 && (
          <div className="details__genres">
            {title.genres.map((g) => (
              <span key={g} className="details__genre-badge">
                {g}
              </span>
            ))}
          </div>
        )}

        {title.director && (
          <div className="details__director">directed by {title.director}</div>
        )}

        {title.cast.length > 0 && (
          <div className="details__cast">{title.cast.join(", ")}</div>
        )}

        {title.synopsis && <p className="details__synopsis">{title.synopsis}</p>}
      </div>

      <div className="details__footer">
        {rateMode && (
          <div className="rate-mode" role="status">
            <span className="rate-mode__label">
              rating queue · {rateMode.position || "–"} of {rateMode.total}
            </span>
            <button
              type="button"
              className="rate-mode__exit rate-mode__exit--big"
              onClick={rateMode.onExit}
            >
              done
            </button>
          </div>
        )}
        <div className="details__state-row">
          <span className="details__state">{stateLine(title)}</span>
          <button
            type="button"
            className="remove-btn"
            disabled={busy || title.list == null}
            title="delete this title from all lists"
            onClick={() => void onRemove()}
          >
            ✕ remove
          </button>
        </div>
        {rateError && (
          <div className="details__rate-error" role="alert">
            {rateError}
          </div>
        )}
        <RatingGrid
          list={title.list}
          rating={title.rating}
          busy={busy}
          onRate={onRate}
          onWatchlist={() => void onWatchlist()}
        />
      </div>
    </div>
  );
}

function stateLine(title: TitleDetail): string {
  const where = title.list ? `on ${title.list}` : "not on any list";
  if (title.rating != null) {
    const label = RATING_LABELS[title.rating];
    return `${where} · rated ${title.rating}${label ? ` — ${label}` : ""}`;
  }
  return `${where} · unrated`;
}

// ── RatingGrid — watch-list button above a 5×2 grid of labelled rating buttons.
// Each button is a legend entry: bold number over its description. 0 is
// contextual ("not watched" — skip on queue/watchlist, unrate on watched).

function RatingGrid({
  list,
  rating,
  busy,
  onRate,
  onWatchlist,
}: {
  list: ListName | null;
  rating: number | null;
  busy: boolean;
  onRate: (rating: number) => void;
  onWatchlist: () => void;
}) {
  const zeroDisabled = busy || list === "ignore" || list == null;
  const zeroTitle =
    list === "queue" || list === "watchlist"
      ? "skip — not watched, move to ignore"
      : list === "watched"
        ? "unrate — clear the rating"
        : "not watched";

  return (
    <div className="rating">
      <div className="rating__actions">
        <button
          type="button"
          className="watchlist-btn"
          disabled={busy || list === "watchlist" || list === "watched"}
          title={
            list === "watchlist"
              ? "already on the watch list"
              : list === "watched"
                ? "already watched"
                : "want to watch — add to the watch list"
          }
          onClick={onWatchlist}
        >
          + watch list
        </button>
      </div>
      <div className="rating__grid" role="group" aria-label="Rate this title">
        {Array.from({ length: 10 }, (_, n) => {
          const isZero = n === 0;
          const current = list === "watched" && rating === n && n > 0;
          const label = isZero ? "not watched" : RATING_LABELS[n];
          return (
            <button
              key={n}
              type="button"
              className={"rating__btn" + (current ? " rating__btn--current" : "")}
              disabled={isZero ? zeroDisabled : busy}
              title={isZero ? zeroTitle : label}
              aria-pressed={current}
              onClick={() => onRate(n)}
            >
              <span className="rating__num">{n}</span>
              <span className="rating__label">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
